import dayjs from "dayjs";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useSelector } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { MyMeetingsResponse } from "../../../../../api/services/program/models";
import { ReactComponent as MyMeetingsIcon } from "../../../../../assets/images/calendar-circle.svg";
import {
  FormController,
  IconText,
  MuiBox,
  MuiSelect,
} from "../../../../../components";
import {
  TimelineActivityDataItem,
  TimelineActivityType,
} from "../../../../../components/timelineActivity/TimelineActivity";
import { TimelineActivityTable } from "../../../../../components/timelineActivityTable/TimelineActivityTable";
import {
  EMPTY_OBJECT,
  getCustomDateFormatWithTime,
  labelKey,
  PAGE_IDS,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import { LabelValue } from "../../../../../helpers/types/common";
import {
  convertToUserFormat,
  convertToUserFormatWithLocalTime,
  getFromToTimeDuration,
  getFullName,
  getValueOrBlank,
  isEmpty,
  isNotEmpty,
} from "../../../../../helpers/utils";
import { useMediaQueries } from "../../../../../hooks/useMediaQueries";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { siteCoreContentLoadingSelector } from "../../../../../redux/slices/appState/appStateSelector";
import { getFormattedDuration, isWithinPeriod } from "../../../helper";
import {
  MY_MEETINGS_TYPE_FILTER_KEYS,
  MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS,
  MyMeetingsFilterFormKeys,
} from "../../../modelsAndSchemas/Models";
import { myMeetingsSiteCoreKeys } from "../../../sitecore/SiteCoreKeys";
import { MyMeetingsWrapper } from "./MyMeetings.style";

const pageId = PAGE_IDS.COACH_PORTAL_DASHBOARD_MY_MEETINGS;

interface MyMeetingsProps {
  meetingsData?: MyMeetingsResponse[];
}

export const MyMeetings: FC<MyMeetingsProps> = ({ meetingsData }) => {
  const {
    commonLabels,
    useFetchLabel,
    fetchComponentTitle,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();
  const userCountryDateFormat = getDateFormatByCountry(
    getValueOrBlank(loggedInUser?.countryCode),
  );
  const { control, getValues, setValue } = useFormContext();
  const siteCoreLoading = useSelector(siteCoreContentLoadingSelector);
  const { type, period } = getValues();
  const { isMobile, isTablet } = useMediaQueries();
  const isToday = useMemo(
    () => period === MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.TODAY,
    [period],
  );

  const tableRef = useRef<{ resetPagination: () => void }>(null);
  const [filteredMeetingsData, setFilteredMeetingsData] = useState<
    TimelineActivityType[]
  >([]);
  const [mappedMeetingsData, setMappedMeetingsData] = useState<
    TimelineActivityType[]
  >([]);

  const {
    myMeetingsTitle,
    periodDropDown,
    typeDropDown,
    sessionsTypeList,
    pastSessionsPeriodList,
    upcomingSessionsPeriodList,
    title: titleLabel,
    mode: modeLabel,
    createdDate: createdDateLabel,
    meetingDate: meetingDateLabel,
    meetingDuration: meetingDurationLabel,
    attendees: attendeesLabel,
    meetingUpdate: meetingUpdateLabel,
    noMeetingsAvailable,
    meetingTypeList,
  } = myMeetingsSiteCoreKeys;

  const [periodListKey, setPeriodListKey] = useState(
    upcomingSessionsPeriodList,
  );

  const getLabel = useFetchLabel(myMeetingsTitle);
  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(myMeetingsTitle, key);
  };

  const formValues = useWatch();

  useEffect(() => {
    applyFilters();
  }, [meetingsData, mappedMeetingsData, formValues]);

  const applyFilters = () => {
    resetPagination();

    const now = dayjs().utc();
    const filteredData = mappedMeetingsData
      .filter((item) => {
        const targetDate = dayjs(item.startDateTime).utc();
        const matchesType =
          type === MY_MEETINGS_TYPE_FILTER_KEYS.UPCOMING_SESSION
            ? targetDate.isAfter(now)
            : targetDate.isBefore(now);
        const matchesPeriod = isWithinPeriod(
          getValueOrBlank(item.startDateTime),
          period,
          type,
        );
        return matchesType && matchesPeriod;
      })
      .sort((a, b) => {
        const dateA = dayjs(a.startDateTime).utc();
        const dateB = dayjs(b.startDateTime).utc();
        return dateB.valueOf() - dateA.valueOf();
      });
    setFilteredMeetingsData(filteredData);
  };

  const transformMeetingData = (
    meetingData: MyMeetingsResponse,
  ): TimelineActivityDataItem[] => {
    const {
      title,
      createdDate,
      durationInSeconds,
      endDateTime,
      meetingUpdate,
      participantFirstName,
      participantLastName,
      startDateTime,
    } = meetingData;

    const getDefaultLabels = () => [
      {
        key: titleLabel,
        value: fetchMyMeetingTitle(title),
      },
      ...(!isMobile && !isTablet ? [EMPTY_OBJECT, EMPTY_OBJECT] : []),
      {
        key: createdDateLabel,
        value: convertToUserFormat(createdDate, userCountryDateFormat),
      },
      {
        key: meetingDateLabel,
        value: convertToUserFormat(startDateTime, userCountryDateFormat),
      },
      {
        key: meetingDurationLabel,
        value: getFormattedDuration(
          startDateTime,
          endDateTime,
          durationInSeconds,
          loggedInUser?.timeZone,
        ),
      },
      {
        key: modeLabel,
        value: commonLabels.online,
      },
      {
        key: attendeesLabel,
        value: getValueOrBlank(
          getFullName(participantFirstName, participantLastName, true),
        ),
      },
      ...(isNotEmpty(meetingUpdate)
        ? [
            {
              key: meetingUpdateLabel,
              value: meetingUpdate,
            },
          ]
        : []),
    ];

    const labels: {
      key: string;
      value: string | LabelValue[];
      listKey?: string;
      replaceEmptyWithDash?: boolean;
    }[] = getDefaultLabels();

    return labels
      .filter(Boolean)
      .map(({ key, value, listKey, replaceEmptyWithDash }) => {
        return {
          type: labelKey,
          label: getLabel(key),
          value: listKey
            ? fetchValueByKeyOfDropdown(
                myMeetingsTitle,
                listKey,
                getValueOrBlank(value as string),
              )
            : value,
          replaceEmptyWithDash: replaceEmptyWithDash,
        };
      });
  };

  const fetchMyMeetingTitle = (title: string) => {
    const titleValue = fetchValueByKeyOfDropdown(
      myMeetingsTitle,
      meetingTypeList,
      title,
    );
    if (isNotEmpty(titleValue)) return titleValue;
    return title;
  };

  useEffect(() => {
    if (!siteCoreLoading && meetingsData && isNotEmpty(meetingsData)) {
      const localData = meetingsData.map((meeting: MyMeetingsResponse) => ({
        timeStamp: convertToUserFormatWithLocalTime(
          meeting.startDateTime,
          getCustomDateFormatWithTime(userCountryDateFormat),
          loggedInUser?.timeZone,
        ),
        fromToTimeDuration: getFromToTimeDuration(
          meeting.startDateTime,
          meeting.endDateTime,
          loggedInUser?.timeZone,
        ),
        startDateTime: meeting.startDateTime,
        endDateTime: meeting.endDateTime,
        id: meeting.coachId,
        title: fetchMyMeetingTitle(meeting.title),
        creator: getFullName(
          meeting.participantFirstName,
          meeting.participantLastName,
          true,
        ),
        isCollapsible: isNotEmpty(meeting),
        expandableData: transformMeetingData(meeting),
        showMoreOptions: false,
        isNotClosureType: true,
      }));

      setMappedMeetingsData(localData);
    } else {
      setMappedMeetingsData([]);
    }
  }, [meetingsData, siteCoreLoading]);

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const handleTypeDropdownChange = (value: string) => {
    setPeriodListKey(
      value === MY_MEETINGS_TYPE_FILTER_KEYS.PAST_SESSION
        ? pastSessionsPeriodList
        : upcomingSessionsPeriodList,
    );
    setValue(
      MyMeetingsFilterFormKeys.period,
      MY_MEETINGS_UPCOMING_PERIOD_FILTER_KEYS.TODAY,
    );
    applyFilters();
  };

  return (
    <MyMeetingsWrapper>
      <MuiBox className="meetings-container" data-testid={pageId}>
        <MuiBox className="title-filter-wrapper">
          <MuiBox className="title-wrapper">
            <IconText
              icon={<MyMeetingsIcon />}
              iconWidth="37px"
              iconHeight="37px"
              typoVariant="h2SemiBold"
              title={fetchComponentTitle(myMeetingsTitle)}
              data-testid={`${pageId}-title`}
            />
          </MuiBox>

          <MuiBox className="filter-wrapper">
            <FormController
              control={control}
              name={MyMeetingsFilterFormKeys.type}
            >
              <MuiSelect
                label={getLabel(typeDropDown)}
                options={getDropdownValues(sessionsTypeList)}
                onChange={(e) =>
                  handleTypeDropdownChange(e.target.value as string)
                }
                isHorizontalLabel={!isMobile}
                id={`${pageId}-sessionsTypeList`}
                data-testid={`${pageId}-sessionsTypeList`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
            <FormController
              control={control}
              name={MyMeetingsFilterFormKeys.period}
            >
              <MuiSelect
                label={getLabel(periodDropDown)}
                options={getDropdownValues(periodListKey)}
                isHorizontalLabel={!isMobile}
                id={`${pageId}-periodList`}
                data-testid={`${pageId}-periodList`}
                placeholder={commonLabels.selectPlaceHolder}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiBox>
        </MuiBox>

        <TimelineActivityTable
          ref={tableRef}
          activityData={filteredMeetingsData}
          isTodaySelected={isToday}
          displayWith={true}
          pageId={pageId}
          noDataInApi={isEmpty(filteredMeetingsData)}
          emptyMessage={getLabel(noMeetingsAvailable)}
        />
      </MuiBox>
    </MyMeetingsWrapper>
  );
};
